import storefrontInstance from '@/api/instances/storefront';
import occInstance from '@/api/instances/occ';
import {UserStatus} from '@/constants/backend';
import {UnitTypes, RideTemperatureUnits} from '@/constants/unit-types';
import {
  IconCardTypes,
  CybersourceApplePayPaymentMethodCode,
  Gr4vyApplePayPaymentMethodCode,
  KCPPaymentMethodCode,
  Gr4vyKCPPaymentMethodCode,
  CybersourceIdealPaymentMethodCode,
  Gr4vyIdealPaymentMethodCode,
} from '@/constants/checkout.js';

const backendModule = {
  namespaced: true,
  state: {
    appleTableHeaders: [],
    b2b: false,
    isAccountConsumerToggleAccessEnabled: true,
    b2cHideBikeModelYearToggle: false,
    cartCode: '',
    cartGuid: '',
    currencyIso: '',
    currentB2BUnit: 'b2c',
    customVariables: [],
    language: '',
    pageTitle: '',
    hostUrl: '',
    userId: '',
    unitId: 'b2c',
    pageCategory: '',
    isConsumerFriendlyMode: false,
    cartToggle: true,
    isAdmin: window.ACC?.isAdmin ?? false,
    isB2BPriceToggle: false,
    isB2Bsite: false,
    contextPath: window.ACC?.config?.contextPath ?? '',
    encodedContextPath: window.ACC?.config?.encodedContextPath ?? '',
    locale: '',
    isB2BPurchasable: window.ACC?.isB2BPurchasable ?? false,
    appleLabelAccess: window.ACC?.appleLabelAccess,
    maxCartItems: window.ACC?.maxCartItems ?? 950,
    isDistributor: window.ACC?.isDistributor ?? false,
    isEmployee: window.ACC?.isEmployee ?? false,
    containerToDelete: null,
    isSpectrumEnabled: false,
    isUserLoggedIn: false,
    trekBrandNavURL: window.ACC?.config.trekBrandNavURL ?? '',
    hasCustomWaterBottleRestriction: false,
    cdnStaticFilesRoot: '',
    CSRFToken: '',
    geolocation: {},
    isLoadingLocation: false,
    supportedPaymentMethods: [],
    isProduction: false,
    isProductCompareEnabledOnSite: false,
    powerReviewsMerchantId: '',
    powerReviewsMerchantGroupId: '',
    powerReviewsApiKey: '',
    powerReviewsLocale: '',
    hasUserAccessToBuyingZone: false,
    currentB2bUnitUserRoles: [],
    kioskModeEnabledOnSite: false,
  },
  mutations: {
    setConsumerFriendlyMode(state, value) {
      state.isConsumerFriendlyMode = value;
    },
    setContainerToDelete(state, value) {
      state.containerToDelete = value;
    },
    setBackendProps(state, payload) {
      Object.entries(payload).forEach(([key, value]) => {
        state[key] = value;
      });
    },
    setIsSpectrumEnabled(state, value) {
      state.isSpectrumEnabled = value;
    },
    setHasCustomWaterBottleRestriction(state, value) {
      state.hasCustomWaterBottleRestriction = value;
    },
    setIsLoadingLocation(state, value) {
      state.isLoadingLocation = value;
    },
  },

  getters: {
    isRestrictedCustomer(state) {
      return state.isEmployee || state.isDistributor;
    },

    unitAbbreviation(state) {
      if (state.preferredUnit === UnitTypes.IMPERIAL.milesString) {
        return UnitTypes.IMPERIAL.milesAbbreviation;
      } else {
        return UnitTypes.METRIC.kilometersAbbreviation;
      }
    },
    userTemperatureUnit(state) {
      if (state.preferredUnit === UnitTypes.IMPERIAL.milesString) {
        return RideTemperatureUnits.FAHRENHEIT;
      } else {
        return RideTemperatureUnits.CELSIUS;
      }
    },
    occUrlParams(state) {
      return {
        userId: state.isUserLoggedIn ? UserStatus.CURRENT_USER : UserStatus.ANONYMOUS_USER,
        cartId: state.isUserLoggedIn ? state.cartCode : state.cartGuid,
      };
    },
    occLocaleParams(state) {
      return {
        lang: state.language,
        curr: state.b2b ? state.currency.isoCode : state.currencyIso,
      };
    },
    getSupportedPaymentMethodCodes(state) {
      let codes = [];
      state.supportedPaymentMethods.forEach((type) => {
        codes.push(type.code);
      });
      return codes;
    },
    getSupportedCardTypeCodes(state) {
      let codes = [];
      state.supportedPaymentMethods.forEach((type) => {
        if (
          type.code !== CybersourceApplePayPaymentMethodCode &&
          type.code !== Gr4vyApplePayPaymentMethodCode &&
          type.code !== KCPPaymentMethodCode &&
          type.code !== Gr4vyKCPPaymentMethodCode &&
          type.code !== CybersourceIdealPaymentMethodCode &&
          type.code !== Gr4vyIdealPaymentMethodCode &&
          !IconCardTypes[type]
        )
          codes.push(type.code);
      });
      return codes;
    },
    countryCode(state) {
      return state.language?.split('_')?.[1]?.toLocaleLowerCase();
    },
    priceFormatter: (state) => {
      // in window.ACC this is an object however not so when injected into backend store
      return state.customVariables
        .filter((item) => item.qualifier.startsWith('priceFormatter.'))
        .reduce((acc, {qualifier, value}) => {
          const keys = qualifier.replace('priceFormatter.', '').split('.');
          const parsedValue = isNaN(value) ? value : Number(value);

          keys.reduce((nested, key, index) => {
            if (index === keys.length - 1) {
              nested[key] = parsedValue;
            } else {
              nested[key] = nested[key] || {};
            }
            return nested[key];
          }, acc);

          return acc;
        }, {});
    },
  },

  actions: {
    async getSupportedPaymentMethods({state}) {
      try {
        const response = await occInstance.get(`cardtypes`);
        state.supportedPaymentMethods = response.data.cardTypes;
      } catch (error) {
        console.error(error);
      }
    },
    toggleConsumerFriendlyMode({commit, state}) {
      let value = !state.isConsumerFriendlyMode;
      storefrontInstance
        .get(`/my-account/price-toggle?priceToggle=${value}`)
        .then(() => {
          commit('setConsumerFriendlyMode', value);
        })
        .catch((error) => {
          console.log(error);
        });
    },
  },
};

export default backendModule;
